import { ReactElement, useEffect, useRef, useState } from 'react';

import api from '~/services/api';
import { IHospitalDetails, IHospitalHistoryItem } from '~/services/api/hospital/types';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Modal from '~/ui/components/common/Modal';
import HistoryContent from './HistoryContent';
import HistoryTitle from './HistoryTitle';

import styles from './HistoryModal.module.scss';

interface IProps {
  patientId: number;
  patientFullName: string;
  onClose: () => void;
}

const HistoryModal = ({ patientId, patientFullName, onClose }: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<IHospitalHistoryItem[]>([]);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const [loadingDetailsIds, setLoadingDetailsIds] = useState<number[]>([]);
  const [detailsByHistoryId, setDetailsByHistoryId] = useState<Record<number, IHospitalDetails>>(
    {},
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const loadHistoryDetails = async (historyId?: number) => {
    if (!historyId || detailsByHistoryId[historyId] || loadingDetailsIds.includes(historyId)) {
      return;
    }

    setLoadingDetailsIds(prev => [...prev, historyId]);

    try {
      const details = await api.hospital.getById(historyId).then(res => res.data);

      if (details && isMountedRef.current) {
        setDetailsByHistoryId(prev => ({
          ...prev,
          [historyId]: details,
        }));
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      if (isMountedRef.current) {
        setLoadingDetailsIds(prev => prev.filter(id => id !== historyId));
      }
    }
  };

  const onAccordionChange = (item: IHospitalHistoryItem, isExpanded: boolean) => {
    setExpandedItemId(isExpanded ? item.id : null);

    if (isExpanded) {
      loadHistoryDetails(item.id);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadHistory = async () => {
      setIsLoading(true);
      setExpandedItemId(null);
      setLoadingDetailsIds([]);
      setDetailsByHistoryId({});

      try {
        const historyItems = await api.hospital
          .getHistoryByPatientId(patientId)
          .then(res => res.data || []);

        if (isMounted) {
          setItems(historyItems);
        }
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [patientId, showError]);

  return (
    <Modal
      open
      maxWidth="md"
      title={<HistoryTitle patientFullName={patientFullName} />}
      className={styles.modal}
      onClose={onClose}
    >
      <HistoryContent
        isLoading={isLoading}
        items={items}
        expandedItemId={expandedItemId}
        loadingDetailsIds={loadingDetailsIds}
        detailsByHistoryId={detailsByHistoryId}
        onAccordionChange={onAccordionChange}
      />
    </Modal>
  );
};

export default HistoryModal;
